import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import "./apiKeys.scss";
import { getCompanies, createCompany, updateCompanyStatus, updateCompany, getCompanyById } from '../../Redux/ActionCreators/settingAction';
import ToggleSwitch from '../../utils/toggleSwitch';

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const renderCustomPagination = (totalPages, currentPage, setCurrentPage) => {
    const pagination = [];

    pagination.push(
        <button className="jv2-page-btn" key="prev" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
    );

    if (currentPage <= 3) {
        pagination.push(
            <button key={1} className={`jv2-page-btn${currentPage === 1 ? ' active' : ''}`} onClick={() => setCurrentPage(1)}>1</button>
        );
    } else {
        pagination.push(<span key="left-dots" className="jv2-page-ellipsis">…</span>);
    }

    const startNum = Math.max(2, currentPage - 1);
    const endNum = Math.min(totalPages - 1, currentPage + 1);

    for (let i = startNum; i <= endNum; i++) {
        pagination.push(
            <button key={i} className={`jv2-page-btn${currentPage === i ? ' active' : ''}`} onClick={() => setCurrentPage(i)}>{i}</button>
        );
    }

    if (currentPage >= totalPages - 2) {
        if (totalPages > 1) {
            pagination.push(
                <button key={totalPages} className={`jv2-page-btn${currentPage === totalPages ? ' active' : ''}`} onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
            );
        }
    } else {
        pagination.push(<span key="right-dots" className="jv2-page-ellipsis">…</span>);
    }

    pagination.push(
        <button className="jv2-page-btn" key="next" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
    );

    return pagination;
};

const Companies = () => {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [form, setForm] = useState({ companyName: '', companyCode: '' });
    const [formErrors, setFormErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

    const SESSION_PAGE_SIZE_KEY = useMemo(() => `companies_pageSize`, []);
    const SESSION_CURRENT_PAGE_KEY = useMemo(() => `companies_currentPage`, []);

    const safeGet = (key, fallback) => {
        try {
            const value = Number(window?.sessionStorage?.getItem(key));
            return Number.isFinite(value) && value > 0 ? value : fallback;
        } catch {
            return fallback;
        }
    };

    const [currentPage, setCurrentPage] = useState(() => safeGet(SESSION_CURRENT_PAGE_KEY, 1));
    const [pageSize, setPageSize] = useState(() => safeGet(SESSION_PAGE_SIZE_KEY, 10));
    const isMobile = window.innerWidth < 767;

    const companies = useSelector(state => state.setting.companies || []);
    const companiesTotal = useSelector(state => state.setting.companiesTotal || 0);

    useEffect(() => {
        fetchCompanies(pageSize, 1);
        setCurrentPage(1);
    }, [pageSize]);

    useEffect(() => {
        sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, pageSize);
    }, [pageSize, SESSION_PAGE_SIZE_KEY]);

    useEffect(() => {
        sessionStorage.setItem(SESSION_CURRENT_PAGE_KEY, currentPage);
    }, [currentPage, SESSION_CURRENT_PAGE_KEY]);

    const fetchCompanies = async (pageSize = 10, page = 1) => {
        setLoading(true);
        await dispatch(getCompanies(pageSize, page));
        setLoading(false);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchCompanies(pageSize, page);
    };

    const handleToggleStatus = async (company) => {
        const newStatus = !company.is_active;
        await dispatch(updateCompanyStatus(company.id, newStatus));
    };

    const handleCreateClick = () => {
        setEditingCompany(null);
        setForm({ companyName: '', companyCode: '' });
        setFormErrors({});
        setShowModal(true);
    };

    const handleEditClick = (company) => {
        setEditingCompany(company);
        setForm({
            companyName: company.name || company.companyName || '',
            companyCode: company.code || company.companyCode || '',
        });
        setFormErrors({});
        setShowModal(true);
    };

    const validate = () => {
        const errors = {};
        if (!form.companyName.trim()) errors.companyName = 'Company name is required';
        if (!form.companyCode.trim()) errors.companyCode = 'Company code is required';
        return errors;
    };

    const handleSave = async () => {
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setSaving(true);

        if (editingCompany) {
            const payload = {
                name: form.companyName.trim(),
                code: form.companyCode.trim(),
            };
            const result = await dispatch(updateCompany(editingCompany.id, payload));
            if (result && result.type === 'UPDATE_COMPANY') {
                await dispatch(getCompanyById(editingCompany.id));
                setShowModal(false);
                setSnack({ open: true, message: 'Company updated successfully!', severity: 'success' });
            } else {
                setSnack({ open: true, message: 'Failed to update company. Please try again.', severity: 'error' });
            }
        } else {
            const payload = {
                name: form.companyName.trim(),
                code: form.companyCode.trim(),
                is_active: true,
            };
            const result = await dispatch(createCompany(payload));
            if (result && result.type === 'CREATE_COMPANY') {
                setShowModal(false);
                setSnack({ open: true, message: 'Company created successfully!', severity: 'success' });
                fetchCompanies(pageSize, 1);
            } else {
                setSnack({ open: true, message: 'Failed to create company. Please try again.', severity: 'error' });
            }
        }

        setSaving(false);
    };

    const totalPages = Math.ceil(companiesTotal / pageSize) || 1;
    const pagedCompanies = companies;

    return (
        <div className="job-table-container">
            <div className="top-navigation">
                <div className='createjob-tab'>
                    <h4>Companies</h4>
                    <button className="create-job" onClick={handleCreateClick} style={{ width: 'unset' }}>
                        Create Company
                    </button>
                </div>
            </div>

            <div className="table-content" style={{ background: '#FFFFFF' }}>
                <div className="jv2-table-outer">
                    <div className="jv2-table-scroll">
                        <table className="jv2-table">
                            <thead>
                                <tr>
                                    <th>Company Name</th>
                                    <th>Company Code</th>
                                    <th>Status</th>
                                    <th>Created At</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="jv2-loading-cell">
                                            <div className="spinner-border" role="status" style={{ color: '#F75891', width: '28px', height: '28px' }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : companies.length === 0 ? (
                                    <tr><td colSpan="5" className="jv2-empty-cell">No companies found.</td></tr>
                                ) : (
                                    pagedCompanies.map(company => (
                                        <tr key={company.id}>
                                            <td>{company.name || company.companyName}</td>
                                            <td>{company.code || company.companyCode}</td>
                                            <td>
                                                <ToggleSwitch
                                                    checked={!!company.is_active}
                                                    onChange={() => handleToggleStatus(company)}
                                                />
                                            </td>
                                            <td>{formatDate(company.created_at || company.createdAt)}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <img
                                                    className="cursor-pointer"
                                                    src="/images/edit-black-icon.svg"
                                                    alt="Actions"
                                                    width={16}
                                                    height={16}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleEditClick(company)}
                                                />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {!loading && (
                        <div className="jv2-pagination">
                            <span className="jv2-pagination-info">
                                {companiesTotal ? (
                                    <>Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, companiesTotal)} of <strong>{companiesTotal}</strong> Companies</>
                                ) : 'Showing 0 of 0'}
                            </span>
                            <div className="jv2-pagination-right">
                                <div className="jv2-page-btns">
                                    {renderCustomPagination(totalPages, currentPage, handlePageChange)}
                                </div>
                                <div className="jv2-per-page">
                                    <span>Per page:</span>
                                    <select
                                        value={pageSize}
                                        onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* CREATE / EDIT COMPANY MODAL */}
            {showModal && (
                <div className="upload-modal automation-modal">
                    <div className="modal-content">
                        <div className="modal-head">
                            <h3>{editingCompany ? 'Edit Company' : 'Create Company'}</h3>
                            <img
                                src="/images/close-icon.svg"
                                alt="close"
                                onClick={() => setShowModal(false)}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                        <div className="modal-input">
                            <div className="automation-dropdown">
                                <label>Company Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Acme Corp"
                                    value={form.companyName}
                                    onChange={e => {
                                        setForm({ ...form, companyName: e.target.value });
                                        setFormErrors({ ...formErrors, companyName: '' });
                                    }}
                                />
                                {formErrors.companyName && (
                                    <span style={{ color: '#e53e3e', fontSize: '12px' }}>
                                        {formErrors.companyName}
                                    </span>
                                )}
                            </div>
                            <div className="automation-dropdown">
                                <label>Company Code *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. ACME001"
                                    value={form.companyCode}
                                    onChange={e => {
                                        setForm({ ...form, companyCode: e.target.value });
                                        setFormErrors({ ...formErrors, companyCode: '' });
                                    }}
                                    disabled={editingCompany}
                                />
                                {formErrors.companyCode && (
                                    <span style={{ color: '#e53e3e', fontSize: '12px' }}>
                                        {formErrors.companyCode}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="d-flex justify-content-end" style={{ margin: '0 30px 20px 30px' }}>
                            <button
                                className="upload-btn"
                                onClick={() => setShowModal(false)}
                                style={{ backgroundColor: 'transparent', border: '1px solid #111928', color: '#111928' }}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                className="upload-btn"
                                onClick={handleSave}
                                style={{ backgroundColor: saving ? '#ccc' : '' }}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
            >
                <Alert severity={snack.severity}>{snack.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default Companies;